import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useActiveCommunity } from '../../../../utility/activeCommunity'
import { useTranslation } from 'react-i18next'

import Modal from 'react-modal'
import PropTypes from 'prop-types'
import moment from 'moment'

import { getProgramsScheduleSummary } from '../../../../actions/programActions'
import { ReactComponent as ModalXIcon } from '../../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as BackArrowIcon } from '../../../../assets/images/common/icons/programBuilder/back-arrow-icon.svg'
import { copyScheduleFromProgram } from '../../../../reducers/programBuilderReducer'
import Pagination from '../../../common/Pagination'
import Loader from '../../../common/loader/legacy-loaders/Op36Loader-web'
import SessionCard from './SessionCard'

import styles from './CopyScheduleModal.module.scss'

const CopyScheduleModal = ({ showModal, closeModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const activeCommunity = useActiveCommunity()
  const timezone = activeCommunity?.timezone
  const communityId = activeCommunity?.id
  const { id, communityPrograms, communityProgramsPagination } = useSelector(state => state.programBuilder)

  const [loader, setLoader] = useState(true)
  const [page, setPage] = useState(1)
  const [selectedProgram, setSelectedProgram] = useState(null)

  const fetchCommunityPrograms = useCallback((newPage = 1) => {
    setLoader(true)
    setPage(newPage)

    dispatch(getProgramsScheduleSummary({ filters: { communityId: communityId, notId: id || '' }, page: newPage, perPage: 10 }))
      .unwrap()
      .finally(() => setLoader(false))
  }, [dispatch, communityId, id])

  useEffect(() => {
    fetchCommunityPrograms()
  }, [fetchCommunityPrograms])

  const handleCopySchedule = () => {
    dispatch(copyScheduleFromProgram({ scheduleSessions: selectedProgram.schedule?.scheduleSessions }))
    closeModal()
  }

  const programTitle = (program) =>
    program.isDraft ? t('programs.builder.copySchedule.programTitleDraft', { name: program.name }) : program.name

  const formatDate = (date) =>
    date ? moment(date).tz(timezone).format('MMMM DD, YYYY') : t('programs.builder.copySchedule.notSelected')

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={closeModal}
      contentLabel={t('programs.builder.copySchedule.modalLabel')}
      className={styles['copy-schedule-modal-content']}
      autoFocus
    >
      <section>
        <div className={`d-flex justify-content-between align-items-center sticky-top ${styles['copy-schedule-header']}`}>
          <div
            className={`d-flex justify-content-center align-items-center ${styles['header-back']}`}
            aria-hidden='true'
            onClick={selectedProgram ? () => setSelectedProgram(null) : closeModal}
          >
            <BackArrowIcon aria-hidden='true' id='backArrowIcon' className='flex-shrink-0 ml-2 mr-3' />
            <p className={styles.title}>{t('programs.builder.copySchedule.title')}</p>
          </div>

          <ModalXIcon className={styles['modal-X-icon']} onClick={closeModal} />
        </div>

        {selectedProgram ? (
          <div className={styles['copy-schedule-selected-program-container']}>
            <p className={styles['program-title']}>{programTitle(selectedProgram)}</p>
            <p className={styles['program-schedule']}>
              {formatDate(selectedProgram.startDate)} -&nbsp;
              {formatDate(selectedProgram.endDate)}
            </p>

            <div className={styles['selected-program-sessions']}>
              {selectedProgram.schedule?.scheduleSessions?.map((session) => (
                <SessionCard key={session.index} session={session} />
              ))}
            </div>

            <div className={`fixed-bottom ${styles['copy-schedule-footer']}`}>
              <div className='row h-100'>
                <div
                  className='col-12 col-md-6 d-flex justify-content-center justify-content-md-end
                              align-items-end align-items-md-center mb-1 mb-md-0'
                >
                  <button
                    className={`btn btn-primary ${styles['go-back-button']}`}
                    aria-hidden='true'
                    onClick={() => setSelectedProgram(null)}
                  >
                    {t('common.action.goBack')}
                  </button>
                </div>

                <div
                  className='col-12 col-md-6 d-flex justify-content-center justify-content-md-start
                              align-items-start align-items-md-center mt-1 mt-md-0'
                >
                  <button
                    className={`btn btn-primary ${styles['copy-schedule-button']}`}
                    aria-hidden='true'
                    onClick={handleCopySchedule}
                  >
                    {t('programs.builder.copySchedule.copyScheduleButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles['copy-schedule-programs-container']}>
            <p className='mb-4'>
              {t('programs.builder.copySchedule.instructions')}
            </p>

            {loader
              ? <Loader message={t('programs.builder.copySchedule.loadingPrograms')} />
              : <>
                <div className={styles.line} />

                <div className={styles['programs-list']}>
                  {communityPrograms.map((program) => (
                    <>
                      <div
                        className={`d-flex justify-content-between align-items-center p-3 ${styles.program}`}
                        aria-hidden='true'
                        onClick={() => { setSelectedProgram(program) }}
                      >
                        <div className='d-flex flex-column'>
                          <p className={styles['program-title']}>{programTitle(program)}</p>
                          <p className={styles['program-schedule']}>
                            {formatDate(program.startDate)} -&nbsp;
                            {formatDate(program.endDate)}
                          </p>
                        </div>

                        <p className={styles['program-sessions']}>
                          {t('programs.builder.copySchedule.scheduleSessionsCount', {
                            count: program.schedule?.scheduleSessions?.length || 0,
                          })}
                        </p>
                      </div>

                      <div className={styles.line} />
                    </>
                  ))}
                </div>
              </>
            }

            {communityProgramsPagination?.totalPages > 1 && (
              <div className='row'>
                <Pagination
                  pageCount={communityProgramsPagination.totalPages}
                  fetchData={fetchCommunityPrograms}
                  activePage={page}
                />
              </div>
            )}
          </div>
        )}
      </section>
    </Modal>
  )
}

CopyScheduleModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

export default CopyScheduleModal
